import debounce from 'lodash/debounce'
import { lazy } from 'react'
import { Dimensions, Platform } from 'react-native'
import * as Yup from 'yup'
import { CardStack } from '../nav/CardStack'
import { PAYMENT_MODE_SUBTYPES, PAYMENT_SCREENS, TEMPLATE } from '../utils/constants'
import { staticStrings } from './string'
import { ScreenItems, Screens } from './types'

const JioPayFinanceHub = lazy(() => import('../screens/JioPayFinanceHub'))
const PayLater = lazy(() => import('../screens/PayLater'))
const EMIStack = lazy(() => import('../nav/EMIStack'))
const UpiStack = lazy(() => import('../nav/UpiStack'))
const NetBanking = lazy(() => import('../screens/NetBanking'))
const Wallet = lazy(() => import('../screens/Wallet'))
const corporateNetBanking = lazy(() => import('../screens/corporateNetBanking'))
const Offers = lazy(() => import('../screens/Offers'))
const ScanAndPay = lazy(() => import('../screens/Offers'))
const FoodCard = lazy(() => import('../screens/FoodCard'))

export const MessageTypes = {
  error: 'NEGATIVE',
  success: 'POSITIVE',
  info: 'NEUTRAL',
}

export const isStatusOK = (data: any) =>
  data?.httpCode === 200 && (data?.status === 'SUCCESS' || data?.status === 'INITIATED')

export const isStatusSuccess = (data: any) => data?.status === 'SUCCESS' || data?.status === 'INITIATED'

export const getNetworkResponseError = (data: any) => {
  console.log('getNetworkResponseError', data)

  let message = 'An unexpected error has occoured'
  let code = -1
  if (data?.error?.message) {
    message = data?.error?.message
    code = data?.error?.code
  } else if (data?.responseData?.message) {
    message = data?.responseData?.message
    code = data?.responseData?.code
  } else if (data?.responseMessage) {
    message = data?.responseMessage
    code = data?.responseCode
  } else if (data?.message) {
    message = data?.message
    code = data?.code
  }

  return { message, code }
}

export const getPaymentType = (paymentType: string) => {
  switch (paymentType) {
    case 'Netbanking':
      return PAYMENT_MODE_SUBTYPES.NB
    case 'Credit/Debit Card':
      return PAYMENT_MODE_SUBTYPES.CARD
    case 'Upi':
      return PAYMENT_MODE_SUBTYPES.UPI
    case 'Wallet':
      return PAYMENT_MODE_SUBTYPES.WALLET
    case 'EMI':
      return PAYMENT_MODE_SUBTYPES.EMI
    case 'Pay Later':
      return PAYMENT_MODE_SUBTYPES.PAYLATER
    default:
      return ''
  }
}

export const ROUTE_NAMES: any = {
  ccdccard: 'Pay via Card | JioPay',
  creditcard: 'Pay via Credit Card | JioPay',
  debitcard: 'Pay via Debit Card | JioPay',
  netbanking: 'Pay via Net Banking | JioPay',
  upi: 'UPI',
  wallet: 'Pay via Wallet | JioPay',
  paylater: 'Pay via Pay Later | JioPay',
  emi: 'Pay via EMI | JioPay',
  sodexo: 'Pay via Sodexo | JioPay',
  offers: 'Pay via Offers | JioPay',
  jioPayFinanceHub: 'JioPay EasyEMI',
  corporateNetbanking: 'Pay via coroprateNetbanking | JioPay',
  scanAndPay: 'Scan and Pay',
}

export const getPaymentMode = (mode: string) => {
  switch (mode.trim()) {
    case 'Pay via Card | JioPay':
      return PAYMENT_SCREENS.ccdccard
    case 'Pay via Credit Card | JioPay':
      return PAYMENT_SCREENS.creditcard
    case 'Pay via Debit Card | JioPay':
      return PAYMENT_SCREENS.debitcard
    case 'Pay via Net Banking | JioPay':
      return PAYMENT_SCREENS.netbanking
    case 'UPI':
      return PAYMENT_SCREENS.upi
    case 'Pay via Wallet | JioPay':
      return PAYMENT_SCREENS.wallet
    case 'Pay via Pay Later | JioPay':
      return PAYMENT_SCREENS.paylater
    case 'Pay via EMI | JioPay':
      return PAYMENT_SCREENS.emi
    case 'Pay via Sodexo | JioPay':
      return PAYMENT_SCREENS.sodexo
    case 'JioPay EasyEMI':
      return 'jiopayfinancehub'
    case 'Pay via coroprateNetbanking | JioPay':
    case 'Pay via coroprateNetbanking | JioPay':
      return PAYMENT_SCREENS.corporateNetbanking
    case 'Pay via Offers | JioPay':
      return PAYMENT_SCREENS.offers
    default:
      return PAYMENT_SCREENS.netbanking
  }
}

export const analyticsEvent = (eventName: string, params?: any) => {
  gtag && gtag('event', eventName, params)
}

export const getPaymentModeList = (compName: string, templateName: string = 'default'): ScreenItems => {
  const name: keyof Screens = compName.replace(' ', '').toLowerCase().trim() as any
  let defaultData = { comp: undefined, name: undefined, title: undefined }

  const card = {
    comp: CardStack,
    name: ROUTE_NAMES.ccdccard,
    title: 'Credit/Debit Card',
  }

  const creditCard = {
    comp: CardStack,
    name: ROUTE_NAMES.creditcard,
    title: 'Credit Card',
  }

  const debitCard = {
    comp: CardStack,
    name: ROUTE_NAMES.debitcard,
    title: 'Debit Card',
  }

  const sodexo = {
    comp: FoodCard,
    header: false,
    name: ROUTE_NAMES.sodexo,
    title: staticStrings.sodexoHeader,
  }
  const jioPayFinanceHub = {
    comp: JioPayFinanceHub,
    name: ROUTE_NAMES.jioPayFinanceHub,
    title: 'JioPay EasyEMI',
  }

  const MilkBasketModelList: any = {
    [PAYMENT_SCREENS.ccdccard]: { ...card },
    [PAYMENT_SCREENS.debitcard]: { ...debitCard },
    [PAYMENT_SCREENS.creditcard]: { ...creditCard },
    [PAYMENT_SCREENS.netbanking]: { comp: NetBanking, name: ROUTE_NAMES.netbanking, title: 'NetBanking' },
    [PAYMENT_SCREENS.upi]: { comp: UpiStack, name: ROUTE_NAMES.upi, title: 'UPI' },
    [PAYMENT_SCREENS.wallet]: { comp: Wallet, name: ROUTE_NAMES.wallet, title: 'Wallet' },
    [PAYMENT_SCREENS.emi]: { comp: EMIStack, name: ROUTE_NAMES.emi, title: 'EMI' },
    [PAYMENT_SCREENS.offers]: { comp: Offers, name: 'Offers', title: 'Offers' },
    [PAYMENT_SCREENS.paylater]: { comp: PayLater, name: ROUTE_NAMES.paylater, title: 'Pay Later' },
    [PAYMENT_SCREENS.sodexo]: { ...sodexo },
    [PAYMENT_SCREENS.scanAndPay]: { comp: Offers, name: ROUTE_NAMES.scanAndPay, title: 'Scan and Pay' },
  }

  const ModeList: any = {
    [PAYMENT_SCREENS.ccdccard]: { ...card },
    [PAYMENT_SCREENS.debitcard]: { ...debitCard },
    [PAYMENT_SCREENS.creditcard]: { ...creditCard },
    [PAYMENT_SCREENS.netbanking]: { comp: NetBanking, name: ROUTE_NAMES.netbanking, title: 'NetBanking' },
    [PAYMENT_SCREENS.upi]: { comp: UpiStack, name: ROUTE_NAMES.upi, title: 'UPI' },
    [PAYMENT_SCREENS.sodexo]: { ...sodexo },
    [PAYMENT_SCREENS.wallet]: { comp: Wallet, name: ROUTE_NAMES.wallet, title: 'Wallet' },
    [PAYMENT_SCREENS.emi]: { comp: EMIStack, name: ROUTE_NAMES.emi, title: 'EMI' },
    [PAYMENT_SCREENS.offers]: { comp: Offers, name: ROUTE_NAMES.offers, title: 'Offers' },
    [PAYMENT_SCREENS.paylater]: { comp: PayLater, name: ROUTE_NAMES.paylater, title: 'Pay Later' },
    jiopayfinancehub: jioPayFinanceHub,
    [PAYMENT_SCREENS.corporateNetbanking]: {
      comp: corporateNetBanking,
      name: ROUTE_NAMES.corporateNetbanking,
      title: 'Corporate Netbanking',
    },
    [PAYMENT_SCREENS.scanAndPay]: { comp: Offers, name: ROUTE_NAMES.scanAndPay, title: 'Scan and Pay' },
  }

  const Template: any = {
    [TEMPLATE.MILK_BASKET]: MilkBasketModelList,
    default: ModeList,
  }

  const template = Template[templateName] || Template.default
  return { ...defaultData, ...template[name] }
}

const UPIIcons: any = {
  '@okaxis': require('../assets/images/ic_gpay.png'),
  '@okhdfcbank': require('../assets/images/ic_gpay.png'),
  '@okicici': require('../assets/images/ic_gpay.png'),
  '@oksbi': require('../assets/images/ic_gpay.png'),
  '@paytm': require('../assets/images/ic_paytm.png'),
  '@ybl': require('../assets/images/ic_phonepe.png'),
  '@ibl': require('../assets/images/ic_phonepe.png'),
  '@axl': require('../assets/images/ic_phonepe.png'),
  '@jio': require('../assets/images/ic_jio.png'),
}

export const allVpa: any = [
  '@abfspay',
  '@andb',
  '@apl',
  '@axis',
  '@axisb',
  '@axisbank',
  '@axisgo',
  '@axl',
  '@barodampay',
  '@barodapay',
  '@boi',
  '@cbin',
  '@cboi',
  '@centralbank',
  '@cnrb',
  '@eazypay',
  '@ezeepay',
  '@fbl',
  '@hdfcbank',
  '@hdfcbankjd',
  '@hsbc',
  '@ibl',
  '@icici',
  '@idbi',
  '@idbibank',
  '@idfcbank',
  '@ikwik',
  '@imobile',
  '@jio',
  '@jkb',
  '@karurvysyabank',
  '@kaypay',
  '@kmb',
  '@kmbl',
  '@kotak',
  '@kvb',
  '@kvbank',
  '@lime',
  '@mahb',
  '@myicici',
  '@obc',
  '@okaxis',
  '@okhdfcbank',
  '@okicici',
  '@oksbi',
  '@pz',
  '@paytm',
  '@payzapp',
  '@pingpay',
  '@pnb',
  '@pockets',
  '@rajgovhdfcbank',
  '@rmhdfcbank',
  '@sbi',
  '@ubi',
  '@uboi',
  '@unionbank',
  '@unionbankofindia',
  '@united',
  '@utbi',
  '@upi',
  '@waaxis',
  '@wahdfcbank',
  '@wasbi',
  '@ybl',
  '@yesbank',
  '@yesbankltd',
]

export const getUPIIcon = (upiId: string) => {
  const upiPartner = upiId?.substring(upiId?.lastIndexOf('@'), upiId?.length)
  return UPIIcons[upiPartner] ?? require('../assets/images/upi_color.png')
}

const getPGType = () => {
  const { REACT_APP_URL = '' } = process.env
  return window.location.href.includes(REACT_APP_URL)
}

export const isIFrame = () => {
  try {
    return window.self !== window.top
  } catch (e) {
    return true
  }
}

export const UPI_SUBSCRIPTION_ENABLED_LIST = [
  'okaxis',
  'okhdfcbank',
  'okicici',
  'oksbi',
  'ybl',
  'ibl',
  'axl',
  'jio',
  'upi',
  'paytm',
]

export enum CardVendors {
  NONE = 'none',
  RUPAY = 'rupay',
  MAESTRO = 'maestro',
  DISCOVER = 'discover',
  DINERS = 'diners',
  MASTER_CARD = 'mastercard',
  AMEX = 'amex',
  VISA = 'visa',
}

export function isSavedCardVisible(cardVendor: CardVendors) {
  switch (cardVendor) {
    case CardVendors.MASTER_CARD:
    case CardVendors.VISA:
    case CardVendors.RUPAY:
      return true
    default:
      return false
  }
}

function amountConverter(value: string) {
  var thousanaVal = ['', 'thousand', 'million', 'billion', 'trillion']
  const oneVal = ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine']
  var tenVal = [
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
  ]
  const twentyVal = ['Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']
  let amount = value.toString()
  let amountLength = amount.indexOf('.')
  if (amountLength === -1) {
    amountLength = amount.length
  }
  if (amountLength > 15) {
    return 'too big'
  }
  const n_val = amount.split('')
  let str_val = ''
  let sk_val = 0
  for (let i = 0; i < amountLength; i++) {
    if ((amountLength - i) % 3 === 2) {
      if (n_val[i] === '1') {
        str_val += tenVal[Number(n_val[i + 1])] + ' '
        i++
        sk_val = 1
      } else if (Number(n_val[i]) !== 0) {
        str_val += twentyVal[Number(n_val[i]) - 2] + ' '
        sk_val = 1
      }
    } else if (Number(n_val[i]) !== 0) {
      str_val += oneVal[Number(n_val[i])] + ' '
      if ((amountLength - i) % 3 === 0) {
        str_val += 'Hundred '
      }
      sk_val = 1
    }
    if ((amountLength - i) % 3 === 1) {
      if (sk_val) {
        str_val += thousanaVal[(amountLength - i - 1) / 3] + ' '
      }
      sk_val = 0
    }
  }
  if (amountLength !== amount.length) {
    const y_val = amount.length
    str_val += 'point '
    for (let i = amountLength + 1; i < y_val; i++) {
      str_val += oneVal[Number(n_val[i])] + ' '
    }
  }
  return str_val.replace(/\s+/g, ' ')
}

export function convertAmountToWords(amount: string) {
  const amountValues = amount.split('.')
  var hundreds = amountConverter(amountValues[0])
  var paisaDecimal = amountValues[1] && amountConverter(amountValues[1])

  if (paisaDecimal) {
    var decimal = paisaDecimal + 'Paisa'
    return hundreds + 'rupees and ' + decimal.trim()
  } else {
    return hundreds.trim() + ' rupees'
  }
}

function join(t: Date, a: any, s: string) {
  function format(m: any) {
    let f = new Intl.DateTimeFormat('en', m)
    return f.format(t)
  }
  return a.map(format).join(s)
}

export function dateConverstion(date: string) {
  const d = new Date(date)

  let a = [{ day: 'numeric' }, { month: 'short' }, { year: 'numeric' }]
  let s = join(d, a, ' ')
  return s
}

const splitingStrategy: any = {
  13: [4, 10, 13], // 4-6-3
  14: [4, 10, 14], // 4-6-4
  15: [4, 10, 15], // 4-6-5
  19: [4, 8, 12, 16, 19], // 4-4-4-4-3
}

export const cardVendorPrefixes = {
  maestro: [
    '502260',
    '504433',
    '504434',
    '504435',
    '504437',
    '504645',
    '504681',
    '504753',
    '504775',
    '504809',
    '504817',
    '504834',
    '504848',
    '504884',
    '504973',
    '504993',
    '508125',
    '508126',
    '508159',
    '508192',
    '508227',
    '56',
    '57',
    '58',
    '600206',
    '603123',
    '603741',
    '603845',
    '622018',
    '67',
    '502045',
    '503898',
  ],
  discover: ['8', '6011', '65', '644', '645', '646', '647', '648', '649'],
  diners: ['300', '301', '302', '304', '305', '309', '36', '38', '39'],
  masterCard: ['5', '22', '23', '24', '25', '26', '27'],
  amex: ['34', '37'],
  visa: ['4'],
  none: ['0'],
}

export const rupayCardMatrix = [
  { start: '5085000000', end: '5089999999' }, // (508[5-9][0-9]{12})
  { start: '6069850000', end: '6079849999' }, // (6069[8-9][0-9]{11})|(607[0-8][0-9]{12})|(6079[0-8][0-9]{11})
  { start: '6080010000', end: '6082009999' }, // (608[0-5][0-9]{12})
  { start: '6082010000', end: '6083009999' }, // covered in prev
  { start: '6083010000', end: '6083509999' }, // covered in prev
  { start: '6083510000', end: '6085009999' }, // covered in prev
  { start: '6521500000', end: '6528499999' },
  // (6521[5-9][0-9]{11})|(652[2-9][0-9]{12})|(6530[0-9]{12})|(6531[0-4][0-9]{11})
  { start: '6528500000', end: '6530499999' }, // covered in prev
  { start: '6530500000', end: '6531499999' }, // covered in prev
  { start: '3528000000', end: '3589999999' }, // covered in prev
  { start: '3000000000', end: '3059999999' },
  // (30[0-5][0-9]{13}) | (3095[0-9]{12}) | (36[0-9]{14}) | (3[8-9][0-9]{14})
  { start: '3095000000', end: '3095999999' }, // covered in prev
  { start: '3600000000', end: '3699999999' }, // covered in prev
  { start: '3800000000', end: '3999999999' }, // covered in prev
  { start: '6011000000', end: '6011039999' },
  // (60110[0-3][0-9]{10}) | (60110[5-9][0-9]{10}) |(6011[2-4][0-9]{11})
  // |(601174[0-9]{10}) | (60117[7-9][0-9]{10}) | (6011[8-9][6-9][0-9]{10})
  { start: '6011050000', end: '6011099999' }, // covered in prev
  { start: '6011200000', end: '6011499999' }, // covered in prev
  { start: '6011740000', end: '6011749999' }, // covered in prev
  { start: '6011770000', end: '6011799999' }, // covered in prev
  { start: '6011860000', end: '6011999999' }, // covered in prev
  { start: '6440000000', end: '6521499999' },
  // ((64[4-8][0-9]{2}|649[0-8][0-9]|6499[0-9]|65[01][0-9]{2}|6520[0-9]|6521[0-4]))([0-9]{11})
  { start: '6531500000', end: '6599999999' }, // ((6531[5-9]|653[2-9][0-9]|65[4-9][0-9]{2}))([0-9]{11})
  { start: '8172000000', end: '8198999999' }, // ((817[2-9]|818[0-9]|819[0-8]))([0-9]{12})
  { start: '8199000000', end: '8201999999' },
]

const prefixListPersist = []

const getPrefixValuesRupay = () => {
  if (prefixListPersist.length > 0) {
    return prefixListPersist
  }
  for (const item of rupayCardMatrix) {
    const panLowLn = Number(item.start.substring(0, 6))
    const panHighLn = Number(item.end.substring(0, 6))
    const diff = panHighLn - panLowLn
    for (let index = 0; index <= diff; index += 1) {
      prefixListPersist.push((panLowLn + index).toString())
    }
  }
  return prefixListPersist
}

const RuPayPrefixList: Array<string> = getPrefixValuesRupay()

export const CardVendorData = [
  {
    vendor: CardVendors.NONE,
    prefix: cardVendorPrefixes.none,
  },
  {
    vendor: CardVendors.DINERS,
    prefix: cardVendorPrefixes.diners,
    imagePath: require('../assets/images/ic_cards_diner.png'),
  },
  {
    vendor: CardVendors.RUPAY,
    prefix: RuPayPrefixList,
  },
  {
    vendor: CardVendors.MAESTRO,
    prefix: cardVendorPrefixes.maestro,
  },
  {
    vendor: CardVendors.DISCOVER,
    prefix: cardVendorPrefixes.discover,
    imagePath: require('../assets/images/ic_cards_discover.png'),
  },
  {
    vendor: CardVendors.MASTER_CARD,
    prefix: cardVendorPrefixes.masterCard,
  },
  {
    vendor: CardVendors.AMEX,
    prefix: cardVendorPrefixes.amex,
  },
  {
    vendor: CardVendors.VISA,
    prefix: cardVendorPrefixes.visa,
  },
]

const hasAnyPrefixes = (cardnumber: string, prefixes: Array<string>) => {
  for (const prefix of prefixes) {
    if (cardnumber.startsWith(prefix)) {
      return true
    }
  }
  return false
}

export const getCardVendorAndImage = (cardNumber: string) => {
  for (const data of CardVendorData) {
    if (hasAnyPrefixes(cardNumber, data.prefix)) {
      return [data.vendor, data.imagePath]
    }
  }
  return [CardVendors.NONE]
}

export const formatCardNumber = (cardNumberWithoutSpace: string, cardLength: number = 16) => {
  let formattedNumber = ''
  let index = 0

  let isSplittingStrategyDefined = false
  let formatArray = []

  if (splitingStrategy[cardLength]) {
    isSplittingStrategyDefined = true
    formatArray = splitingStrategy[cardLength].slice()
  }

  while (index < cardNumberWithoutSpace.length) {
    // If any other specific pattern is required
    if (isSplittingStrategyDefined) {
      if (index !== 0 && index % formatArray[0] === 0) {
        formatArray.shift()
        formattedNumber += ' '
      }
    }
    // For usual 16 digit long card number
    else {
      if (index !== 0 && index % 4 === 0) {
        formattedNumber += ' '
      }
    }
    formattedNumber += cardNumberWithoutSpace.charAt(index)
    index += 1
  }
  return formattedNumber
}

export const getExpiryMessage = (data: string): string => {
  const monthDate = data.replace(' / ', '/')
  const monthStr = monthDate.split('/')[0]
  const yearStr = monthDate.split('/')[1]
  const month = Number(monthStr)
  const year = Number(`20${yearStr}`)
  const today = new Date()
  const MAX_EXPIRY_YEARS = 10
  if (isNaN(month) || isNaN(year)) {
    return 'Invalid Expiry Date'
  }
  if (month > 12 && month < 1) {
    return 'Invalid Expiration month'
  }
  if (!(year > today.getFullYear() || (year === today.getFullYear() && month >= today.getMonth() + 1))) {
    return 'expiration year is in the past'
  }
  if (!(today.getFullYear() + MAX_EXPIRY_YEARS > year)) {
    return 'Invalid Expiry Date'
  }

  return ''
}

export const getWindow = () => {
  const lwindow: any = window
  if (lwindow.webkit) {
    return lwindow?.webkit?.messageHandlers
  } else {
    return lwindow
  }
}

export const sendErrorIfNotIframe = debounce(
  (status: string, message: string, redirectUrl: string, intentId: string) => {
    if (getWindow().JioPaymentWebViewInterface) {
      console.log('HTAG', 'IN2')
      getWindow().JioPaymentWebViewInterface.postMessage(
        JSON.stringify({ event: 'SEND_ERROR', data: { status_code: status, error_msg: message, intentId } }),
      )
    } else if (!isIFrame() && redirectUrl) {
      const separator = redirectUrl.includes('?') ? '&' : '?'
      const returnUrl =
        redirectUrl +
        `${separator}status=` +
        status +
        '&message=' +
        message +
        '&intentid=' +
        intentId +
        '&tid=' +
        intentId
      window.location.replace(returnUrl)
    }
  },
  500,
)

const getImageLocation = (type: string) => {
  const bankids = new Map([
    ['ICICI', '-130px 0px'],
    ['HDFC', '-160px 0px'],
    ['SBI', '-195px 0px'],
    ['UTI', '-8px 0px'],
    ['KOTAK', '-35px 0px'],
  ])

  return bankids.get(type) ?? -1
}

const addTwoDecimalZeroes = (num: any) => {
  const dec = num.split('.')
  if (dec.length >= 1) {
    const len = dec && dec.length > 2 ? dec.length : 2
    return Number(num).toFixed(len) + ''
  } else {
    return num + ''
  }
}

const CardScheme = {
  VISA: 104,
  MASTER_CARD: 105,
  RUPAY: 199,
  DINERS: 107,
  AMEX: 103,
  CUP: 133,
  DISCOVER: 108,
  EuroPay: 106,
  JCB: 109,
  SODEXO: 4000, //bankId
}

export const getCardLogo = (cardId: number) => {
  switch (cardId) {
    case CardScheme.DINERS:
      return '../assets/images/ic_cards_diner.png'
    case CardScheme.DISCOVER:
      return '../assets/images/ic_cards_discover.png'
    default:
      return undefined
  }
}

export const returnRegexSchema = (regex: any, errorMsg: string) => {
  const returnRegex = Yup.string().test('Test if its a valid', errorMsg, (value: string) => {
    if (value === undefined) {
      return false
    }
    return regex.test(value)
  })

  return returnRegex
}
export const CARD_TYPE = {
  DEBIT_CARD: '110',
  CREDIT_CARD: '130',
  FOOD_CARD: '140',
}

export const getCardType = (type: string) => {
  if (type === CARD_TYPE.CREDIT_CARD) {
    return 'Credit Card'
  } else if (type === CARD_TYPE.DEBIT_CARD) {
    return 'Debit Card'
  } else if (type === CARD_TYPE.FOOD_CARD) {
    return staticStrings.sodexoHeader
  }
  return ''
}

let prevExpLength = 0
export const formatExpiryDate = (expiryWithoutSpace: string) => {
  let formattedExpiry = ''
  let index = 0
  if (expiryWithoutSpace.length === 1 && parseInt(expiryWithoutSpace, 10) > 1) {
    expiryWithoutSpace = '0' + expiryWithoutSpace
  }
  if (expiryWithoutSpace.length === 2 && parseInt(expiryWithoutSpace, 10) > 12) {
    expiryWithoutSpace = '0' + expiryWithoutSpace
  }
  while (index < expiryWithoutSpace.length) {
    formattedExpiry += expiryWithoutSpace.charAt(index)
    if (index !== 0 && prevExpLength !== expiryWithoutSpace.length && index === 1) {
      formattedExpiry += ' / '
    }
    index += 1
  }
  prevExpLength = expiryWithoutSpace.length
  return formattedExpiry
}

export const getCardVendor = (scheme: number) => {
  console.log('===> getCardVendor scheme', scheme)
  switch (scheme) {
    case CardScheme.VISA:
      return 'visa'
    case CardScheme.MASTER_CARD:
      return 'mastercard'
    case CardScheme.RUPAY:
      return 'rupay'
    case CardScheme.DINERS:
      return 'diners'
    case CardScheme.AMEX:
      return 'amex'
    case CardScheme.DISCOVER:
      return 'discover'
    case CardScheme.JCB:
      return 'jcb'
    case CardScheme.EuroPay:
      return 'europay'
    case CardScheme.CUP:
      return 'cup'
    case CardScheme.SODEXO:
      return 'sodexo'
    default:
      return 'none'
  }
}

const convertHexToRGBA = (hexCode: any, opacity: any) => {
  let hex = hexCode.replace('#', '')

  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
  }

  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  return `rgba(${r},${g},${b},${opacity / 100})`
}

const isValidColorCode = (colorCode: string) => {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/i.test(colorCode)
}

export { addTwoDecimalZeroes, convertHexToRGBA, getImageLocation, getPGType, isValidColorCode }

//It will format the amount with comma separated and two fractional digits
export const formatAmount = (amount = '0') => {
  if (!amount) {
    amount = '0'
  }
  return Number(parseFloat(amount).toFixed(2)).toLocaleString('en', {
    minimumFractionDigits: 2,
  })
}

export const isIE = () => {
  if (Platform.OS === 'android' || Platform.OS === 'ios') {
    return false
  }
  const doc: any = document
  return /*@cc_on!@*/ false || !!doc?.documentMode
}

export const cardFormatSpacersLength = {
  none: 3,
  rupay: 3,
  maestro: 4,
  discover: 3,
  mastercard: 3,
  diners: 3,
  amex: 2,
  visa: 3,
}

export const getKeyboardType = (dataType: string) => {
  if (dataType === 'email') {
    return 'email-address'
  } else if (dataType === 'number') {
    return 'number-pad'
  } else if (dataType === 'float') {
    return 'decimal-pad'
  } else if (dataType === 'NV') {
    return 'name-phone-pad'
  }
  return 'default'
}
export const maxCardLength = {
  none: 16,
  rupay: 16,
  maestro: 19,
  discover: 16,
  mastercard: 16,
  diners: 16, // diners us and canada has 16 digits which is not supported now
  amex: 15,
  visa: 16,
}

export const maxCVVLength: any = {
  none: 3,
  rupay: 3,
  maestro: 3,
  discover: 3,
  mastercard: 3,
  diners: 3,
  amex: 4,
  visa: 3,
}

export const convertToCamelCase = (str: string) => {
  const result = str.replace(/([A-Z])/g, ' $1')
  return result.charAt(0).toUpperCase() + result.slice(1)
}

export const convertCamelCaseToString = (text: string) => {
  var result = text.replace(/([A-Z])/g, '$1')
  return result.charAt(0).toUpperCase() + result.slice(1)
}
export const isIosDeviceWebCheck = () => {
  return (
    /iPad|iPhone|iPod/.test(navigator.platform) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

export const getBrowserName = () => {
  const userAgent = navigator.userAgent
  let browserName
  if (userAgent.match(/chrome|chromium|crios/i)) {
    browserName = 'chrome'
  } else if (userAgent.match(/firefox|fxios/i)) {
    browserName = 'firefox'
  } else if (userAgent.match(/safari/i)) {
    browserName = 'safari'
  } else if (userAgent.match(/opr\//i)) {
    browserName = 'opera'
  } else if (userAgent.match(/edg/i)) {
    browserName = 'edge'
  } else {
    browserName = 'No browser detection'
  }
  return browserName
}

export const getOsName = () => {
  let OSName = 'Unknown OS'
  if (navigator.userAgent.indexOf('Win') !== -1) {
    OSName = 'Windows'
  }
  if (navigator.userAgent.indexOf('Mac') !== -1) {
    OSName = 'Macintosh'
  }
  if (navigator.userAgent.indexOf('Linux') !== -1) {
    OSName = 'Linux'
  }
  if (navigator.userAgent.indexOf('Android') !== -1) {
    OSName = 'Android'
  }
  if (navigator.userAgent.indexOf('like Mac') !== -1) {
    OSName = 'iOS'
  }

  const browserName = getBrowserName()
  //Windows, Chrome-108.0.0.0
  return `${OSName}-${browserName}`
}
export const getScreenDimen = () => {
  const isLargeScreen = Dimensions.get('window').width > 550
  const isFullScreen =
    window.location.href.includes('/pg/loader.html') || window.location.href.includes('/pg/redirectToBank')
  if (isLargeScreen && !isFullScreen) {
    return {
      height: 640,
      width: 360,
    }
  }
  return {
    width: Dimensions.get('window').width,
    height: Dimensions.get('window').height,
  }
}

export const isUpiNumber = (value: string): boolean => {
  return value && /^\d{8,15}$/.test(value) ? true : false
}

export const routeEventMapper: any = {
  'Pay via Card | JioPay': 'card',
  'Pay via Credit Card | JioPay': 'card',
  'Pay via Debit Card | JioPay': 'card',
  'Pay via Net Banking | JioPay': 'netbanking',
  P: 'upi',
  'Pay via Wallet | JioPay': 'wallet',
  'Pay via Pay Later | JioPay': 'paylater',
  'Pay via EMI | JioPay': 'emi',
  'Pay via Sodexo | JioPay': 'sodexo',
  'Pay via Offers | JioPay': 'offers',
  'Jio Pay Finance Hub': 'jiofinhub',
  'Pay via coroprateNetbanking | JioPay': 'corporateNetbanking',
}

export const findBestMatch = (
  searchObject: any,
  dataArray: any[] = [],
  priorityArray: any[] = [],
  genericSymbol: string,
  nestedKey: string,
) => {
  const scoreArray = dataArray.map((obj) => {
    let score = 0
    let matches = 0
    const item = nestedKey ? obj[nestedKey] : obj
    const searchKeyLength = priorityArray.length
    priorityArray.forEach((key, index) => {
      if (searchObject[key] === item[key]) {
        score = score + (searchKeyLength - index)
        matches = matches >= 0 ? matches + 1 : matches
      } else if (item[key] !== genericSymbol) {
        matches = -1
      }
    })
    return {
      score,
      matches,
      data: obj,
    }
  })
  const filteredArray = scoreArray.filter((item) => {
    return item.matches > 0
  })
  const descCompare = (a, b) => {
    if (a.matches > b.matches) {
      return -1
    }
    if (a.matches === b.matches) {
      return a.score >= b.score ? -1 : 1
    }
    return 1
  }
  const sortedArray = filteredArray.sort(descCompare)
  return sortedArray[0]?.data
}

//"2024-09-10T16:25:31+0530"
export const convertDateInto = (threeMinutesLater: Date) => {
  const year = threeMinutesLater.getFullYear()
  const month = String(threeMinutesLater.getMonth() + 1).padStart(2, '0') // Months are zero-based
  const day = String(threeMinutesLater.getDate()).padStart(2, '0')
  const hours = String(threeMinutesLater.getHours()).padStart(2, '0')
  const minutes = String(threeMinutesLater.getMinutes()).padStart(2, '0')
  const seconds = String(threeMinutesLater.getSeconds()).padStart(2, '0')

  // Get the timezone offset
  const timezoneOffsetMinutes = threeMinutesLater.getTimezoneOffset()
  const timezoneOffsetHours = Math.abs(Math.floor(timezoneOffsetMinutes / 60))
    .toString()
    .padStart(2, '0')
  const timezoneOffsetSign = timezoneOffsetMinutes >= 0 ? '-' : '+'

  // Construct the formatted date string
  const formattedDate = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${timezoneOffsetSign}${timezoneOffsetHours}00`
  return formattedDate
}

export const isMobileDevice = function () {
  let check = false;
  (function (a) { if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) check = true; })(navigator.userAgent || navigator.vendor || window.opera);
  return check;
}
